import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Typography } from '@mui/material';
import Countdown from 'common/components/Countdown';
import CustomIconButton from 'common/components/CustomIconButton';
import CustomMobileFitHighlight from 'common/components/CustomMobileFitHighlight';
import CustomTooltip from 'common/components/CustomTooltip';
import SoundWave from 'common/components/SoundWave';
import useFlexBoxStyles from 'common/flexBox';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Webcam from 'react-webcam';
import { getAudioDeviceId, getVideoDeviceId, setDevices } from 'redux/slices/shortVideoSlice';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import ShortVideoSettingsMenu from './settings/ShortVideoSettingsMenu';

const useStyles = makeStyles()((theme) => {
    const fade = keyframes`
    from {
        opacity: 0;
    }`;

    return {
        button: {
            '& > div': {
                borderStyle: 'solid',
                borderWidth: '8px 0 8px 16px',
                borderRadius: '100%',
                borderColor: '#e52d27',
            },
        },
        startAnimate: {
            '& > div': {
                transition: '.218s ease',
                animation: `${fade} 1s infinite alternate`,
            },
            '&:hover ': {
                backgroundColor: theme.palette.common.white,
                '& > div': {
                    animationPlayState: 'paused',
                    opacity: '1 !important',
                    borderRadius: '2px',
                    borderColor: '#000',
                },
            },
        },

        timeChip: {
            background: '#FFFFFF66',
        },
        mB4: {
            marginBottom: theme.spacing(4),
        },

        videoContainer: {
            '& video': {
                backgroundColor: theme.palette.common.black,
                width: '100%',
                height: 'auto',
                maxHeight: '60vh',
            },
        },
        timer: {
            width: theme.spacing(2),
            textAlign: 'end',
        },
    };
});

const TIME_LIMIT = 60;

const ShortVideoFormRecording = ({ onSubmit, step, uploading, progress }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();

    const [settingsOpen, setSettingsOpen] = useState(false);
    const webcamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const videoEl = useRef(null);
    const videoDeviceId = useSelector(getVideoDeviceId);
    const audioDeviceId = useSelector(getAudioDeviceId);
    const [capturing, setCapturing] = useState(false);
    const [recordedChunks, setRecordedChunks] = useState([]);
    const [videoUrl, setVideoUrl] = useState();
    const [anchorEl, setAnchorEl] = useState(null);
    const [showCountdown, setShowCountdown] = useState(false);

    const handleDevices = useCallback(
        (mediaDevices) => {
            dispatch(setDevices(mediaDevices));
        },
        [dispatch],
    );

    useEffect(() => {
        navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }, [handleDevices]);

    useEffect(() => {
        if (recordedChunks.length) {
            const blob = new Blob(recordedChunks, {
                type: 'video/webm',
            });
            const url = URL.createObjectURL(blob);
            setVideoUrl(url);
        } else {
            setVideoUrl();
        }
    }, [recordedChunks]);

    const handleCapture = () => (capturing ? handleStopCaptureClick() : setShowCountdown(true));

    const handleDataAvailable = useCallback(
        ({ data }) => {
            if (data.size > 0) {
                setRecordedChunks((prev) => prev.concat(data));
            }
        },
        [setRecordedChunks],
    );

    const [timePassed, setTimePassed] = useState(0);
    const [timeLeft, setTimeLeft] = useState(TIME_LIMIT);
    const [timerInterval, setTimerInterval] = useState(null);

    const increaseTime = useCallback(() => {
        setTimePassed((t) => t + 1);
    }, []);

    const startTimer = useCallback(() => {
        setTimerInterval(
            setInterval(() => {
                increaseTime();
            }, 1000),
        );
    }, [increaseTime]);

    const handleStopCaptureClick = useCallback(() => {
        mediaRecorderRef.current.stop();
        setCapturing(false);
        clearInterval(timerInterval);
    }, [mediaRecorderRef, setCapturing, timerInterval]);

    useEffect(() => {
        if (timeLeft === 0) {
            clearInterval(timerInterval);
            handleStopCaptureClick();
        }
    }, [timeLeft, handleStopCaptureClick, timerInterval]);

    useEffect(() => {
        setTimeLeft(TIME_LIMIT - timePassed);
    }, [timePassed]);

    const handleStartCaptureClick = useCallback(() => {
        setShowCountdown(false);
        setCapturing(true);
        startTimer();
        mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
            mimeType: 'video/webm',
        });
        mediaRecorderRef.current.addEventListener('dataavailable', handleDataAvailable);
        mediaRecorderRef.current.start();
    }, [webcamRef, setCapturing, mediaRecorderRef, handleDataAvailable, startTimer]);

    const handleDelete = () => {
        setTimeLeft(TIME_LIMIT);
        setTimePassed(0);
        setRecordedChunks([]);
    };

    const handleSubmit = () => {
        const videoBlob = new Blob(recordedChunks, {
            type: 'video/webm',
        });
        const videoFile = new File([videoBlob], `video_${new Date().toISOString()}.webm`, {
            type: 'video/webm',
        });
        onSubmit(videoFile, videoEl.current.duration);
    };

    const handleSettingsClicked = (event) => {
        setAnchorEl(event.currentTarget);
        setSettingsOpen(true);
    };

    const handleSettingsOnClose = () => {
        setSettingsOpen(false);
        setAnchorEl(null);
    };

    return (
        <div>
            <div className={flexBox.relative}>
                {!videoUrl && (
                    <div
                        className={cx(
                            flexBox.absolute,
                            flexBox.right2,
                            flexBox.top2,
                            flexBox.zIndex2,
                            flexBox.flex,
                            flexBox.alignItemsCenter,
                            flexBox.gap2,
                        )}
                    >
                        <SoundWave />

                        {!capturing && !showCountdown && (
                            <CustomTooltip arrow placement={'left'} title={t('common.components.breadcrumb.config')}>
                                <CustomIconButton
                                    onClick={handleSettingsClicked}
                                    className={classes.appIconButton}
                                    size="medium"
                                >
                                    <SettingsOutlinedIcon component="svg" />
                                </CustomIconButton>
                            </CustomTooltip>
                        )}
                        {settingsOpen && (
                            <ShortVideoSettingsMenu
                                open={true}
                                handleOnClose={handleSettingsOnClose}
                                anchorEl={anchorEl}
                            />
                        )}
                    </div>
                )}

                <div className={cx(classes.videoContainer, flexBox.relative)}>
                    {!videoUrl && (
                        <>
                            <Webcam
                                muted={true}
                                audio={true}
                                ref={webcamRef}
                                // The the ideal options are very important to help the browser to select the must suitable camera,
                                // otherwise the browser might select a low resolution camera or a camera in 4:3 aspect ratio first,
                                // just because it is the first camera in the devices list.
                                videoConstraints={{
                                    deviceId: videoDeviceId,
                                    width: { ideal: 1920 },
                                    height: { ideal: 1080 },
                                }}
                                audioConstraints={{ deviceId: audioDeviceId }}
                            />
                            {showCountdown && <Countdown onFinished={handleStartCaptureClick} />}
                            {capturing && webcamRef.current?.video && (
                                <CustomMobileFitHighlight
                                    video={{
                                        height: webcamRef.current?.video?.clientHeight,
                                        width: webcamRef.current?.video?.clientWidth,
                                    }}
                                />
                            )}
                        </>
                    )}
                    {videoUrl && (
                        <video id="video-replay" src={videoUrl} ref={videoEl} controls crossOrigin="anonymous" />
                    )}
                </div>
            </div>

            {step === 0 && (
                <div
                    className={cx(
                        flexBox.flex,
                        flexBox.relative,
                        flexBox.justifyContentCenter,
                        flexBox.alignItemsCenter,
                        flexBox.padding2,
                        flexBox.gap2,
                    )}
                >
                    {Boolean(recordedChunks.length) && (
                        <CustomTooltip
                            arrow
                            title={t('license.licenseTableRow.delete')}
                            aria-label="delete license"
                            placement="bottom"
                        >
                            <CustomIconButton size="medium" id="onlive-button-delete" onClick={handleDelete}>
                                <DeleteOutlinedIcon component="svg" />
                            </CustomIconButton>
                        </CustomTooltip>
                    )}

                    {!recordedChunks.length && (
                        <CustomTooltip
                            arrow
                            title={capturing ? t('Stop recording') : t('Start recording')}
                            placement={'bottom'}
                        >
                            <CustomIconButton
                                size="medium"
                                disabled={showCountdown}
                                onClick={handleCapture}
                                className={cx(
                                    flexBox.flex,
                                    flexBox.alignItemsCenter,
                                    flexBox.justifyContentCenter,
                                    classes.button,
                                    capturing && classes.startAnimate,
                                )}
                            >
                                <div></div>
                            </CustomIconButton>
                        </CustomTooltip>
                    )}

                    {Boolean(recordedChunks.length) && (
                        <CustomTooltip
                            arrow
                            title={t('shortVideo.form.check')}
                            aria-label="delete license"
                            placement="bottom"
                        >
                            <CustomIconButton size="medium" id="onlive-button-delete" onClick={handleSubmit}>
                                <CheckOutlinedIcon component="svg" />
                            </CustomIconButton>
                        </CustomTooltip>
                    )}

                    <div className={cx(flexBox.flex, flexBox.alignItemsCenter, flexBox.absolute, flexBox.right3)}>
                        <TimerOutlinedIcon component="svg" />
                        <Typography className={classes.timer}>{videoUrl ? TIME_LIMIT - timeLeft : timeLeft}</Typography>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShortVideoFormRecording;
